import type { BrowserInfo } from '../content/tips';
import { SHORT_HASH_LENGTH } from '../entropy/hash';
import { formatOneIn, type Band } from '../entropy/score';
import type { RunResult } from '../run';
import { copyText, flash, h, joinList, nextId } from './dom';
import { contributorPhrases } from './export';
import { forget, formatSavedDate, loadSaved, save } from './remember';

export const BAND_TITLE: Record<Band, string> = {
  low: 'Low exposure',
  moderate: 'Moderate exposure',
  high: 'High exposure',
};

export const BAND_TEXT: Record<Band, string> = {
  low: 'Strong anti-fingerprinting detected. Many signals are blocked, randomized or standardized, so you blend into a large crowd.',
  moderate: 'Some protections are active, but several stable, high-entropy signals remain. You are distinctive, though less so than a default browser.',
  high: 'This looks like a typical default browser. Its combined signals are likely unique among a large population: enough to recognize you across sites without cookies.',
};

function rememberControls(currentHash: string): HTMLElement {
  const statusId = nextId('remember-status');
  const status = h('p', { class: 'remember-status', id: statusId, role: 'status' });
  const box = h('input', { type: 'checkbox', id: nextId('remember'), 'aria-describedby': statusId });
  const replace = h('button', { type: 'button', class: 'link-button', hidden: true }, 'Replace saved result with this one');

  const describe = (justSaved = false) => {
    const saved = loadSaved();
    box.checked = !!saved;
    replace.hidden = true;
    if (saved && justSaved) {
      status.textContent = 'Saved this hash and today’s date on this device. Run the check again later, or after changing a setting, to compare.';
    } else if (!saved) {
      status.textContent = 'Nothing is stored. Tick the box to keep only this hash and the date on this device.';
    } else if (saved.hash === currentHash) {
      status.textContent = `Same hash as the result you saved on ${formatSavedDate(saved.savedAt)}. A tracker would recognize this browser too.`;
    } else {
      status.textContent = `Different from the result you saved on ${formatSavedDate(saved.savedAt)} (${saved.hash.slice(0, SHORT_HASH_LENGTH)}). Something about your browser changed since then.`;
      replace.hidden = false;
    }
  };

  box.addEventListener('change', () => {
    if (box.checked) {
      if (!save(currentHash)) {
        box.checked = false;
        status.textContent = 'Could not save: storage is blocked in this browser.';
        return;
      }
    } else {
      forget();
    }
    describe(box.checked);
  });
  replace.addEventListener('click', () => {
    save(currentHash);
    describe(true);
  });
  describe();

  return h(
    'div',
    { class: 'remember' },
    h('label', { class: 'checkbox' }, box, ' Remember this result on this device'),
    status,
    replace,
  );
}

export function renderSummary(result: RunResult, browser: BrowserInfo, onAbout: () => void): HTMLElement {
  const { score } = result;
  const shortHash = result.stable.hex.slice(0, SHORT_HASH_LENGTH);
  const phrases = contributorPhrases(result);
  const defences = result.protections.filter((p) => p.strength !== 'signal');
  const privacySignals = result.protections.filter((p) => p.strength === 'signal');
  const copyStatus = h('span', { class: 'visually-hidden', role: 'status' });

  const copyButton = h('button', { type: 'button', class: 'button small' }, 'Copy');
  copyButton.addEventListener('click', async () => {
    flash(copyButton, (await copyText(result.stable.hex)) ? 'Copied' : 'Copy failed', copyStatus);
  });

  const aboutButton = h(
    'button',
    { type: 'button', class: 'info-button', title: 'About the score' },
    h('span', { 'aria-hidden': 'true' }, 'i'),
    h('span', { class: 'visually-hidden' }, 'About the score'),
  );
  aboutButton.addEventListener('click', onAbout);

  return h(
    'div',
    { class: 'summary-card', 'data-band': score.band },
    h(
      'div',
      { class: 'summary-band' },
      h('h2', { id: 'summary-title' }, h('span', { class: 'band-pill' }, BAND_TITLE[score.band])),
      h('p', null, BAND_TEXT[score.band]),
    ),
    h(
      'div',
      { class: 'summary-stats' },
      h(
        'section',
        { class: 'stat', 'aria-labelledby': 'stat-bits' },
        h('h3', { id: 'stat-bits' }, 'Estimated identifying bits ', h('span', { class: 'estimate-tag' }, '(estimate)'), ' ', aboutButton),
        h('p', { class: 'stat-value' }, score.totalBits.toFixed(1), h('span', { class: 'stat-unit' }, ' bits')),
        h(
          'p',
          { class: 'stat-sub' },
          `Roughly ${formatOneIn(score.oneIn)} browsers would share this combination. A rough, upper-bound-style estimate from published averages, not a measurement.`,
        ),
      ),
      h(
        'section',
        { class: 'stat', 'aria-labelledby': 'stat-hash' },
        h('h3', { id: 'stat-hash' }, 'Stable fingerprint hash'),
        result.stable.hex
          ? h('p', { class: 'stat-value mono hash' }, h('span', { title: result.stable.hex }, shortHash), ' ', copyButton, copyStatus)
          : h('p', { class: 'signal-note' }, `Could not compute the hash: ${result.stable.error ?? 'Web Crypto unavailable'}.`),
        h(
          'p',
          { class: 'stat-sub' },
          'If this hash is the same on your next visit, a tracker could recognize you without cookies. Change a setting and run the check again to see it change.',
        ),
        result.stable.hex ? rememberControls(result.stable.hex) : null,
      ),
    ),
    phrases.length
      ? h('p', { class: 'contributors' }, `Your ${joinList(phrases)} ${phrases.length === 1 ? 'accounts' : 'account'} for most of your fingerprint.`)
      : null,
    h(
      'div',
      { class: 'protections' },
      h('h3', null, 'Protections detected'),
      defences.length
        ? h('ul', { class: 'chips' }, defences.map((p) => h('li', { class: `chip chip-${p.strength}`, title: p.detail }, p.label)))
        : h('p', { class: 'muted' }, 'None detected. Your browser hands out canvas, WebGL, audio and the other high-entropy signals unchanged.'),
      privacySignals.length ? h('p', { class: 'muted' }, `Privacy settings: ${privacySignals.map((p) => p.label).join(' · ')}`) : null,
    ),
    result.changedBetweenRuns.length
      ? h(
          'p',
          { class: 'volatility' },
          h('strong', null, 'Changed between two samples: '),
          `${result.changedBetweenRuns.join(', ')}. These were read twice a moment apart and came back different, so they cannot identify you across visits.`,
        )
      : h('p', { class: 'volatility muted' }, 'Every stable signal returned the same value on a second sample, so no per-read randomization was seen.'),
    h('p', { class: 'muted small' }, `Detected browser: ${browser.name}. Collected in ${(result.durationMs / 1000).toFixed(1)} s, two samples.`),
  );
}
